"use client";

import { useState } from "react";
import Close from "@mui/icons-material/Close";
import { Button, IconButton } from "@mui/material";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { getAssetUrl } from "@/utils/assetUrl";
import { trackCtaClick } from "@/utils/commonEvents";
import { appendSessionUtmsToUrl } from "@/utils/utms";

const TALK_TO_CLINICIAN_URL = "https://sleep.resmed.com/#insurance-check-section";

export interface TreatmentCardData {
  id: string;
  titleKey: string;
  descriptionKey: string;
  imageUrl: string;
  imageAltKey: string;
}

interface TreatmentCardProps {
  data: TreatmentCardData;
  onInteraction?: () => void;
}

export default function TreatmentCard({
  data,
  onInteraction,
}: TreatmentCardProps) {
  const t = useTranslations("results-page.treatment-cards");
  const tAria = useTranslations("aria");

  const [isExpanded, setIsExpanded] = useState(false);
  const treatmentTitle = t(data.titleKey);

  const talkToClinicianUrl = appendSessionUtmsToUrl(TALK_TO_CLINICIAN_URL);

  return (
    <article
      className="treatment-card"
      aria-label={tAria("treatment-card", { treatment: treatmentTitle })}
    >
      <Image
        className="treatment-card-image"
        src={getAssetUrl(data.imageUrl)}
        alt={t(data.imageAltKey)}
        fill
        unoptimized
      />
      <div className="treatment-card-gradient" aria-hidden="true" />

      <div className="treatment-card-content">
        <h4 className="treatment-card-title">{treatmentTitle}</h4>
        {!isExpanded && (
          <Button
            variant="contained"
            color="secondary"
            className="treatment-card-learn-more-button"
            onClick={() => {
              trackCtaClick({
                componentName: "sleep_assessment_pro",
                clickedArea: "Learn more",
                clickedText: t("learn-more"),
              });
              setIsExpanded(true);
              onInteraction?.();
            }}
            aria-label={tAria("learn-more-about", {
              treatment: treatmentTitle,
            })}
          >
            {t("learn-more")}
          </Button>
        )}
      </div>

      {isExpanded && (
        <div className="treatment-card-popup">
          <p className="treatment-card-popup-description">
            {t.rich(data.descriptionKey, {
              sup: (chunks) => <sup>{chunks}</sup>,
            })}
          </p>
          <div className="treatment-card-popup-actions">
            <Button
              variant="contained"
              className="treatment-card-cta-button"
              aria-label={tAria("talk-to-clinician-about", {
                treatment: treatmentTitle,
              })}
              href={talkToClinicianUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackCtaClick({
                  componentName: "sleep_assessment_pro",
                  clickedArea: "Talk to a clinician",
                  clickedText: t("talk-to-a-clinician"),
                  clickedUrl: talkToClinicianUrl,
                })
              }
            >
              {t("talk-to-a-clinician")}
            </Button>
            <IconButton
              onClick={() => setIsExpanded(false)}
              aria-label={tAria("close-treatment-details")}
            >
              <Close aria-hidden focusable={false} />
            </IconButton>
          </div>
        </div>
      )}
    </article>
  );
}
